import Dialog from "@/shared/components/dialog/Dialog"
import type { ManagedDialogComponent, ManagedDialogControls, ManagedDialogInjectedProps } from "./types"

type DialogValue<Props extends object, Value> = Value | ((props: Props) => Value)

type ManagedDialogOptions<Props extends object> = {
  title: DialogValue<Props, string>
  description?: DialogValue<Props, string | undefined>
  contentClassName?: DialogValue<Props, string | undefined>
  bodyClassName?: DialogValue<Props, string | undefined>
  // Default for dialogs that should always require an explicit action to close.
  // Individual openDialog calls can still override it.
  persistent?: boolean
  renderBody: (props: Props, controls: ManagedDialogControls) => React.ReactNode
  renderFooter?: (props: Props, controls: ManagedDialogControls) => React.ReactNode
}

type ManagedDialogHook<Props extends object> = (props: Props & ManagedDialogControls) => ManagedDialogOptions<Props>

export function createManagedDialog<Props extends object>(
  optionsOrHook: ManagedDialogOptions<Props> | ManagedDialogHook<Props>
): ManagedDialogComponent<Props> {
  return function ManagedDialog(props: Props & ManagedDialogInjectedProps) {
    const { close, isOpen, onOpenChange, persistent, ...dialogProps } = props
    const controls: ManagedDialogControls = { close, isOpen, onOpenChange }
    const options =
      typeof optionsOrHook === "function" ? optionsOrHook({ ...(dialogProps as Props), ...controls }) : optionsOrHook

    return (
      <Dialog
        title={resolveDialogValue(options.title, dialogProps as Props)}
        description={options.description && resolveDialogValue(options.description, dialogProps as Props)}
        contentClassName={
          options.contentClassName && resolveDialogValue(options.contentClassName, dialogProps as Props)
        }
        bodyClassName={options.bodyClassName && resolveDialogValue(options.bodyClassName, dialogProps as Props)}
        persistent={persistent ?? options.persistent}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        renderBody={() => options.renderBody(dialogProps as Props, controls)}
        renderFooter={options.renderFooter ? () => options.renderFooter?.(dialogProps as Props, controls) : undefined}
      />
    )
  }
}

function resolveDialogValue<Props extends object, Value>(value: DialogValue<Props, Value>, props: Props) {
  if (typeof value === "function") {
    return (value as (props: Props) => Value)(props)
  }

  return value
}
